import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import {
  CalendarCheck, ArrowLeft, Save, Users, Calendar as CalendarIcon,
  Search, CheckCircle2, XCircle, Copy, Sun, PartyPopper, Clock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status model (Feb 2026 revision):
//   Present   → paid working day
//   Absent    → unpaid (LOP) — needs reason
//   Half-day  → 0.5 paid day — needs reason
//   Holiday   → paid (Sundays + festivals) — needs reason
// Legacy "Leave" rows in older DB records still render correctly in the
// monthly grid and reports, but the new entry UI never creates them.
const STATUS_LIST = [
  { code: "P",  label: "Present",  test: "P",  cls: "bg-emerald-600 hover:bg-emerald-700 text-white", soft: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: CheckCircle2 },
  { code: "A",  label: "Absent",   test: "A",  cls: "bg-red-600 hover:bg-red-700 text-white",         soft: "bg-red-500/15 text-red-300 border-red-500/40",             icon: XCircle },
  { code: "½",  label: "Half-day", test: "HD", cls: "bg-amber-600 hover:bg-amber-700 text-white",     soft: "bg-amber-500/15 text-amber-300 border-amber-500/40",      icon: Clock },
  { code: "H",  label: "Holiday",  test: "H",  cls: "bg-blue-600 hover:bg-blue-700 text-white",       soft: "bg-blue-500/15 text-blue-300 border-blue-500/40",         icon: PartyPopper },
];
const LABEL_TO_CODE = Object.fromEntries(STATUS_LIST.map(s => [s.label, s.code]));

// Context-aware reason chips
const ABSENT_REASONS = [
  "Sick", "Medical Emergency", "Personal Work", "Family Function",
  "Casual Leave", "Without Information", "Other"
];
const HALFDAY_REASONS = [
  "Late arrival", "Early leaving", "Sick (recovered mid-day)",
  "Personal Work", "Medical Appointment", "Other"
];
const HOLIDAY_REASONS = [
  "Weekly Off (Sunday)", "Republic Day", "Independence Day", "Diwali",
  "Pongal", "Christmas", "New Year", "Public Holiday", "Festival", "Other"
];
const reasonsFor = (status) => {
  if (status === "Holiday") return HOLIDAY_REASONS;
  if (status === "Half-day") return HALFDAY_REASONS;
  return ABSENT_REASONS;
};
const needsReasonFor = (status) =>
  status === "Absent" || status === "Half-day" || status === "Holiday";

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const prevDayISO = (iso) => {
  // Parse as local date to avoid UTC→local off-by-one in IST/other timezones.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  const ny = dt.getFullYear();
  const nm = String(dt.getMonth() + 1).padStart(2, "0");
  const nd = String(dt.getDate()).padStart(2, "0");
  return `${ny}-${nm}-${nd}`;
};
// Saturdays are working days for this customer — only Sundays are weekly off.
// Parse ISO date components manually so "2026-02-15" is always evaluated in
// local time (avoids UTC midnight → Saturday-in-IST off-by-one).
const isSunday = (iso) => {
  if (!iso) return false;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};

// ---------------- DAILY VIEW ----------------
const DailyView = ({ employees, refreshKey, bumpRefresh }) => {
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState({}); // { employee_name: {status, overtime_hours, absent_reason, id} }
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadForDate = useCallback(async (targetDate, mode = "load") => {
    try {
      const res = await axios.get(`${API}/hr/attendance`, { params: { date: targetDate } });
      const byName = {};
      (res.data || []).forEach(r => {
        byName[r.employee_name] = {
          id: r.id || "",
          status: r.status || "",
          overtime_hours: r.overtime_hours ? String(r.overtime_hours) : "",
          absent_reason: r.absent_reason || "",
        };
      });
      const next = {};
      employees.forEach(e => {
        next[e.name] = byName[e.name] || { id: "", status: "", overtime_hours: "", absent_reason: "" };
      });
      // Smart defaults — only on fresh load (no existing records) and only if user requests
      if (mode === "all-present") {
        employees.forEach(e => {
          if (!next[e.name].status) next[e.name].status = "Present";
        });
        toast.success("All employees marked Present — change the absentees");
      } else if (mode === "all-holiday") {
        employees.forEach(e => {
          next[e.name] = { ...next[e.name], status: "Holiday", absent_reason: next[e.name].absent_reason || (isSunday(targetDate) ? "Weekly Off (Sunday)" : "Public Holiday") };
        });
        toast.success("All employees marked Holiday (paid)");
      } else if (mode === "copy-prev") {
        const prevRes = await axios.get(`${API}/hr/attendance`, { params: { date: prevDayISO(targetDate) } });
        const prevByName = {};
        (prevRes.data || []).forEach(r => { prevByName[r.employee_name] = r; });
        let copied = 0;
        employees.forEach(e => {
          const p = prevByName[e.name];
          if (p && !next[e.name].status) {
            next[e.name] = {
              ...next[e.name],
              status: p.status || "",
              overtime_hours: p.overtime_hours ? String(p.overtime_hours) : "",
              absent_reason: p.absent_reason || "",
            };
            copied++;
          }
        });
        toast.success(`Copied ${copied} entries from ${prevDayISO(targetDate)}`);
      } else if (mode === "load") {
        // Auto-default: if this is a Sunday and no records exist yet, fill
        // every employee with Holiday + "Weekly Off (Sunday)" reason.
        // Operator can still override individual rows.
        const hasAnyRecord = (res.data || []).length > 0;
        if (isSunday(targetDate) && !hasAnyRecord) {
          employees.forEach(e => {
            next[e.name] = { ...next[e.name], status: "Holiday", absent_reason: "Weekly Off (Sunday)" };
          });
        }
      }
      setRows(next);
      setLoaded(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    }
  }, [employees]);

  useEffect(() => { if (employees.length) loadForDate(date, "load"); /* eslint-disable-next-line */ }, [date, employees, refreshKey]);

  const setField = (name, field, val) => {
    setRows(prev => ({ ...prev, [name]: { ...prev[name], [field]: val } }));
  };
  const setStatus = (name, label) => {
    setRows(prev => {
      const cur = prev[name] || {};
      const next = { ...cur, status: label };
      if (label === "Present") next.absent_reason = "";
      return { ...prev, [name]: next };
    });
  };

  const filteredEmployees = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return employees;
    return employees.filter(e => e.name.toLowerCase().includes(s));
  }, [search, employees]);

  const summary = useMemo(() => {
    const tally = { Present: 0, Absent: 0, "Half-day": 0, Holiday: 0, Unmarked: 0 };
    employees.forEach(e => {
      const s = rows[e.name]?.status;
      if (s && tally[s] !== undefined) tally[s]++;
      else tally.Unmarked++;
    });
    return tally;
  }, [rows, employees]);

  const handleSave = async () => {
    const payload = employees
      .map(e => ({
        employee_name: e.name,
        date,
        status: rows[e.name]?.status || "",
        overtime_hours: parseFloat(rows[e.name]?.overtime_hours) || 0,
        absent_reason: rows[e.name]?.absent_reason || "",
        source: "manual",
      }))
      .filter(r => r.status);
    if (!payload.length) { toast.error("Mark at least one employee"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/hr/attendance/bulk-upload`, { rows: payload });
      toast.success(`Saved: ${res.data.created} new · ${res.data.updated} updated`);
      bumpRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="manual-attendance-daily">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-gray-400">Attendance Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="bg-gray-800 border-gray-600 text-white h-9 w-44 [color-scheme:dark]"
            data-testid="daily-date-input" />
        </div>
        <Button size="sm" onClick={() => loadForDate(date, "all-present")}
          className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="mark-all-present-btn">
          <CheckCircle2 className="w-4 h-4 mr-1" /> Mark all Present
        </Button>
        <Button size="sm" onClick={() => loadForDate(date, "all-holiday")}
          className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="mark-all-holiday-btn">
          <Sun className="w-4 h-4 mr-1" /> {isSunday(date) ? "Mark all Holiday (Sunday)" : "Mark all Holiday"}
        </Button>
        <Button size="sm" onClick={() => loadForDate(date, "copy-prev")}
          className="bg-purple-600 hover:bg-purple-700 h-9" data-testid="copy-prev-day-btn">
          <Copy className="w-4 h-4 mr-1" /> Copy from {prevDayISO(date)}
        </Button>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-gray-400">Search Employee</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Type a name..."
              className="bg-gray-800 border-gray-600 text-white h-9 pl-8"
              data-testid="employee-search-input" />
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        {STATUS_LIST.map(s => (
          <span key={s.label} className={`px-2.5 py-1 rounded-full border ${s.soft}`} data-testid={`summary-chip-${s.label.toLowerCase()}`}>
            {s.label}: <span className="font-bold ml-1">{summary[s.label]}</span>
          </span>
        ))}
        <span className="px-2.5 py-1 rounded-full border bg-gray-700/30 text-gray-300 border-gray-600" data-testid="summary-chip-unmarked">
          Unmarked: <span className="font-bold ml-1">{summary.Unmarked}</span>
        </span>
        <span className="ml-auto text-gray-400">{employees.length} employees · {isSunday(date) ? <span className="text-blue-300">Sunday (weekly off)</span> : "Working day"}</span>
      </div>

      {/* Employee list */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-0">
          {!loaded ? (
            <div className="text-center text-gray-500 py-8">Loading...</div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No employees match</div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filteredEmployees.map((emp, idx) => {
                const row = rows[emp.name] || {};
                const curCode = LABEL_TO_CODE[row.status] || "";
                const needsReason = needsReasonFor(row.status);
                const presetList = reasonsFor(row.status);
                return (
                  <div key={emp.name} className="flex flex-wrap items-center gap-3 px-3 py-2.5 hover:bg-gray-800/30"
                    data-testid={`daily-row-${idx}`}>
                    <div className="w-7 text-gray-600 text-xs">{idx + 1}</div>
                    <div className="flex-1 min-w-[140px]">
                      <div className="text-white text-sm font-medium" data-testid={`daily-emp-name-${idx}`}>{emp.name}</div>
                      {emp.designation && <div className="text-[10px] text-gray-500">{emp.designation}</div>}
                    </div>
                    {/* Status pills */}
                    <div className="flex gap-1" role="group" aria-label="status">
                      {STATUS_LIST.map(s => (
                        <button
                          key={s.code}
                          type="button"
                          onClick={() => setStatus(emp.name, s.label)}
                          className={`px-2.5 py-1 rounded text-xs font-bold transition ${curCode === s.code ? s.cls : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}
                          title={s.label}
                          data-testid={`status-btn-${idx}-${s.test}`}
                        >
                          {s.code}
                        </button>
                      ))}
                    </div>
                    {/* OT hours — available for any marked status so a Half-day
                        or even a Leave-day can still record overtime if the
                        employee worked extra hours. */}
                    {row.status && (
                      <div className="flex items-center gap-1">
                        <Label className="text-[10px] text-gray-500">OT</Label>
                        <Input type="number" step="0.5" min="0"
                          value={row.overtime_hours || ""}
                          onChange={e => setField(emp.name, "overtime_hours", e.target.value)}
                          placeholder="0"
                          className="bg-gray-800 border-gray-700 text-cyan-300 text-xs h-8 w-16"
                          data-testid={`ot-input-${idx}`}
                        />
                        <span className="text-[10px] text-gray-500">hrs</span>
                      </div>
                    )}
                    {/* Reason (when not Present) */}
                    {needsReason && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm"
                            className="h-8 border-amber-700/50 text-amber-300 hover:bg-amber-900/30 text-xs"
                            data-testid={`reason-trigger-${idx}`}>
                            {row.absent_reason || "+ Add Reason"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-gray-900 border-amber-700/40 w-72 p-3 space-y-2" align="end">
                          <div className="text-xs text-amber-300 font-semibold">Reason for {row.status}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {presetList.map(r => (
                              <button key={r} type="button"
                                onClick={() => setField(emp.name, "absent_reason", r)}
                                className={`px-2 py-1 rounded border text-[11px] transition ${
                                  row.absent_reason === r
                                    ? "bg-amber-500/20 border-amber-400 text-amber-200"
                                    : "border-gray-700 text-gray-300 hover:border-amber-500/40"
                                }`}
                                data-testid={`reason-preset-${idx}-${r.replace(/\s+/g,'-').toLowerCase().replace(/[()]/g,'')}`}
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                          <Input value={row.absent_reason || ""}
                            onChange={e => setField(emp.name, "absent_reason", e.target.value)}
                            placeholder="Or type a custom reason..."
                            className="bg-gray-800 border-gray-700 text-white text-xs h-8"
                            data-testid={`reason-custom-${idx}`}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-gray-950/90 backdrop-blur border-t border-gray-800 flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving}
          className="bg-teal-600 hover:bg-teal-700" data-testid="daily-save-btn">
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving..." : `Save ${Object.values(rows).filter(r => r.status).length} entries`}
        </Button>
      </div>
    </div>
  );
};

// ---------------- MONTHLY VIEW ----------------
const MonthlyView = ({ employees, refreshKey, bumpRefresh }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]); // raw
  const [grid, setGrid] = useState({}); // {empName: {dayNum: {status, reason, ot, id}}}
  const [saving, setSaving] = useState(false);
  const [reasonPopover, setReasonPopover] = useState(null); // {empName, day}

  const daysInMonth = new Date(year, month, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  const fetchMonth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/attendance`, { params: { month, year } });
      setRecords(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Load failed");
    }
  }, [month, year]);

  useEffect(() => { if (employees.length) fetchMonth(); }, [fetchMonth, employees, refreshKey]);

  useEffect(() => {
    const g = {};
    employees.forEach(e => { g[e.name] = {}; });
    records.forEach(r => {
      const d = parseInt((r.date || "").split("-")[2], 10);
      if (!g[r.employee_name]) g[r.employee_name] = {};
      g[r.employee_name][d] = {
        id: r.id || "",
        status: r.status || "",
        overtime_hours: r.overtime_hours || 0,
        absent_reason: r.absent_reason || "",
      };
    });
    setGrid(g);
  }, [records, employees]);

  const cycleCell = (empName, day) => {
    // Cycle: blank → Present → Absent → Half-day → Holiday → blank
    const order = ["", "Present", "Absent", "Half-day", "Holiday"];
    const cur = grid[empName]?.[day]?.status || "";
    const idx = order.indexOf(cur);
    const next = order[(idx === -1 ? 0 : (idx + 1)) % order.length];
    setGrid(prev => ({
      ...prev,
      [empName]: { ...prev[empName], [day]: { ...(prev[empName]?.[day] || {}), status: next, absent_reason: next === "Present" ? "" : (prev[empName]?.[day]?.absent_reason || "") } },
    }));
    // Auto-open the popover for any marked status so the operator can enter
    // OT (always) and a reason (when status ≠ Present). Closes only when
    // status cycles back to blank.
    if (next) {
      setReasonPopover({ empName, day });
    } else {
      setReasonPopover(null);
    }
  };

  const setReason = (empName, day, reason) => {
    setGrid(prev => ({
      ...prev,
      [empName]: { ...prev[empName], [day]: { ...(prev[empName]?.[day] || {}), absent_reason: reason } },
    }));
  };

  const setCellOT = (empName, day, val) => {
    setGrid(prev => ({
      ...prev,
      [empName]: { ...prev[empName], [day]: { ...(prev[empName]?.[day] || {}), overtime_hours: val } },
    }));
  };

  const fillSundaysHoliday = () => {
    const g = { ...grid };
    employees.forEach(e => {
      g[e.name] = { ...(g[e.name] || {}) };
      days.forEach(d => {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        if (isSunday(iso)) {
          const cur = g[e.name][d];
          if (!cur || !cur.status) {
            g[e.name][d] = { ...(cur || {}), status: "Holiday", absent_reason: cur?.absent_reason || "Weekly Off (Sunday)" };
          }
        }
      });
    });
    setGrid(g);
    toast.success("Sundays marked Holiday (paid)");
  };

  const fillMonthPresent = () => {
    const g = { ...grid };
    employees.forEach(e => {
      g[e.name] = { ...(g[e.name] || {}) };
      days.forEach(d => {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        if (isSunday(iso)) return; // Saturdays are working days; only skip Sundays
        const cur = g[e.name][d];
        if (!cur || !cur.status) g[e.name][d] = { ...(cur || {}), status: "Present" };
      });
    });
    setGrid(g);
    toast.success("Working days marked Present (Sundays untouched)");
  };

  const handleSave = async () => {
    const payload = [];
    employees.forEach(e => {
      days.forEach(d => {
        const cell = grid[e.name]?.[d];
        if (!cell || !cell.status) return;
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        payload.push({
          employee_name: e.name,
          date: dateStr,
          status: cell.status,
          overtime_hours: parseFloat(cell.overtime_hours) || 0,
          absent_reason: cell.absent_reason || "",
          source: "manual",
        });
      });
    });
    if (!payload.length) { toast.error("Nothing to save"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/hr/attendance/bulk-upload`, { rows: payload });
      toast.success(`Saved: ${res.data.created} new · ${res.data.updated} updated`);
      bumpRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const monthsLabels = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const cellClassFor = (status) => {
    if (status === "Present") return "bg-emerald-600/30 text-emerald-200 border-emerald-500/40";
    if (status === "Absent") return "bg-red-600/30 text-red-200 border-red-500/40";
    if (status === "Holiday") return "bg-blue-600/30 text-blue-200 border-blue-500/40";
    // Legacy values still rendered correctly if they exist in older DB rows
    if (status === "Half-day") return "bg-amber-600/30 text-amber-200 border-amber-500/40";
    if (status === "Leave") return "bg-purple-600/30 text-purple-200 border-purple-500/40";
    return "bg-gray-800/40 text-gray-500 border-gray-700 hover:bg-gray-700/40";
  };

  return (
    <div className="space-y-4" data-testid="manual-attendance-monthly">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-gray-400">Month</Label>
          <Select value={String(month)} onValueChange={v => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-32 h-9" data-testid="monthly-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600">
              {monthsLabels.map((m, i) => <SelectItem key={m} value={String(i + 1)} className="text-white">{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-400">Year</Label>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v, 10))}>
            <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-24 h-9" data-testid="monthly-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-600">
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={fillMonthPresent}
          className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="monthly-fill-present-btn">
          <CheckCircle2 className="w-4 h-4 mr-1" /> Fill working days Present
        </Button>
        <Button size="sm" onClick={fillSundaysHoliday}
          className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="monthly-fill-weekends-btn">
          <Sun className="w-4 h-4 mr-1" /> Mark Sundays as Holiday (paid)
        </Button>
        <span className="text-[11px] text-gray-500 ml-auto">Click cell to cycle Present → Absent → Half-day → Holiday → blank. Popover opens automatically for Absent / Half-day / Holiday.</span>
      </div>

      <div className="border border-gray-800 rounded overflow-auto max-h-[60vh]">
        <table className="text-[11px]">
          <thead className="bg-gray-900 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 text-left text-gray-400 sticky left-0 bg-gray-900 z-20 min-w-[160px]">Employee</th>
              {days.map(d => {
                const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const sun = isSunday(iso);
                return (
                  <th key={d} className={`px-1 py-1 font-mono ${sun ? "text-blue-400" : "text-gray-400"}`}>{d}</th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.name} className="border-t border-gray-800">
                <td className="px-2 py-1 text-white font-medium sticky left-0 bg-gray-900/95 z-10 whitespace-nowrap">{emp.name}</td>
                {days.map(d => {
                  const cell = grid[emp.name]?.[d] || {};
                  // Legacy codes for old DB values (Half-day → ½, Leave → L)
                  const legacyCode = cell.status === "Leave" ? "L" : null;
                  const code = legacyCode || (cell.status ? LABEL_TO_CODE[cell.status] : "·");
                  const popOpen = reasonPopover?.empName === emp.name && reasonPopover?.day === d;
                  const isMarked = !!cell.status;
                  const presetList = reasonsFor(cell.status);
                  const hasOT = parseFloat(cell.overtime_hours) > 0;
                  const showReason = needsReasonFor(cell.status);
                  return (
                    <td key={d} className="p-0.5">
                      <Popover open={popOpen} onOpenChange={(o) => { if (!o) setReasonPopover(null); }}>
                        <PopoverAnchor asChild>
                          <button
                            type="button"
                            onClick={() => cycleCell(emp.name, d)}
                            onContextMenu={(e) => { e.preventDefault(); if (isMarked) setReasonPopover({ empName: emp.name, day: d }); }}
                            className={`w-7 h-7 rounded border text-center font-bold ${cellClassFor(cell.status)} ${(cell.absent_reason || hasOT) ? "ring-1 ring-amber-400/50" : ""}`}
                            title={cell.status ? `${cell.status}${cell.absent_reason ? ` — ${cell.absent_reason}` : ""}${hasOT ? ` · OT ${cell.overtime_hours}h` : ""}` : "Click to mark"}
                            data-testid={`monthly-cell-${emp.name.replace(/\s+/g, '_')}-${d}`}
                          >
                            {code}
                          </button>
                        </PopoverAnchor>
                        {isMarked && (
                          <PopoverContent className="bg-gray-900 border-amber-700/40 w-72 p-3 space-y-3" align="center">
                            <div className="text-xs text-amber-300 font-semibold">{cell.status} — {emp.name} · Day {d}</div>
                            <div>
                              <Label className="text-[10px] text-gray-400">Overtime hours</Label>
                              <Input
                                type="number" step="0.5" min="0"
                                value={cell.overtime_hours || ""}
                                onChange={e => setCellOT(emp.name, d, e.target.value)}
                                placeholder="0"
                                className="bg-gray-800 border-gray-700 text-cyan-300 text-xs h-8 mt-0.5"
                                data-testid={`monthly-ot-input-${emp.name.replace(/\s+/g, '_')}-${d}`}
                              />
                            </div>
                            {showReason && (
                              <div>
                                <Label className="text-[10px] text-gray-400">Reason</Label>
                                <div className="flex flex-wrap gap-1.5 mt-0.5">
                                  {presetList.map(r => (
                                    <button key={r} type="button"
                                      onClick={() => { setReason(emp.name, d, r); }}
                                      className={`px-2 py-1 rounded border text-[11px] transition ${
                                        cell.absent_reason === r
                                          ? "bg-amber-500/20 border-amber-400 text-amber-200"
                                          : "border-gray-700 text-gray-300 hover:border-amber-500/40"
                                      }`}>
                                      {r}
                                    </button>
                                  ))}
                                </div>
                                <Input value={cell.absent_reason || ""}
                                  onChange={e => setReason(emp.name, d, e.target.value)}
                                  placeholder="Custom reason..."
                                  className="bg-gray-800 border-gray-700 text-white text-xs h-8 mt-1.5" />
                              </div>
                            )}
                          </PopoverContent>
                        )}
                      </Popover>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-600/40 border border-emerald-500/40" />Present (paid)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-600/40 border border-red-500/40" />Absent (LOP)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-600/40 border border-amber-500/40" />Half-day (½ pay)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-600/40 border border-blue-500/40" />Holiday (paid)</span>
        <span className="text-amber-300/80">A ring = reason or OT recorded</span>
      </div>

      <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-gray-950/90 backdrop-blur border-t border-gray-800 flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving}
          className="bg-teal-600 hover:bg-teal-700" data-testid="monthly-save-btn">
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Saving..." : "Save month"}
        </Button>
      </div>
    </div>
  );
};

// ---------------- ROOT ----------------
const HRManualAttendance = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [mode, setMode] = useState("daily"); // daily | monthly
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey(k => k + 1);

  useEffect(() => {
    axios.get(`${API}/hr/employees`).then(r => {
      const active = (r.data || []).filter(e => e.is_active !== false);
      setEmployees(active);
    }).catch(() => toast.error("Failed to load employees"));
  }, []);

  return (
    <div className="space-y-4" data-testid="hr-manual-attendance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/hr/attendance")}
            className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="back-to-attendance-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-bold text-teal-400 flex items-center gap-2">
              <CalendarCheck className="w-5 h-5" /> Manual Attendance Entry
            </h2>
            <p className="text-gray-400 text-sm">Pick a date → tap status pills → save. No spreadsheets needed.</p>
          </div>
        </div>
        <div className="inline-flex rounded-lg border border-gray-700 bg-gray-900 p-1">
          <button
            onClick={() => setMode("daily")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${mode === "daily" ? "bg-teal-600 text-white" : "text-gray-400 hover:text-white"}`}
            data-testid="mode-daily-btn"
          >
            <Users className="w-4 h-4" /> Daily
          </button>
          <button
            onClick={() => setMode("monthly")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${mode === "monthly" ? "bg-teal-600 text-white" : "text-gray-400 hover:text-white"}`}
            data-testid="mode-monthly-btn"
          >
            <CalendarIcon className="w-4 h-4" /> Monthly
          </button>
        </div>
      </div>

      {employees.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700">
          <CardContent className="p-8 text-center text-gray-400">Loading employees...</CardContent>
        </Card>
      ) : mode === "daily" ? (
        <DailyView employees={employees} refreshKey={refreshKey} bumpRefresh={bumpRefresh} />
      ) : (
        <MonthlyView employees={employees} refreshKey={refreshKey} bumpRefresh={bumpRefresh} />
      )}
    </div>
  );
};

export default HRManualAttendance;
